import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from "@exhousing/design-system";
import { ArrowLeft, Megaphone, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { LifecycleStepper, type LifecycleStep } from "@/components/LifecycleStepper";
import { DescriptionsCard } from "@/components/property/DescriptionsCard";
import { FeaturesCard } from "@/components/property/FeaturesCard";
import { ManagementCard } from "@/components/property/ManagementCard";
import { MediaGallery } from "@/components/property/MediaGallery";
import { MeterReadingsCard } from "@/components/property/MeterReadingsCard";
import { PropertyLifecycleCard } from "@/components/property/PropertyLifecycleCard";
import { PropertySectionCard } from "@/components/property/PropertySectionCard";
import { PublicationCard } from "@/components/property/PublicationCard";
import { PublishReadinessCard } from "@/components/property/PublishReadinessCard";
import { RoomsCard } from "@/components/property/RoomsCard";
import { useMe } from "@/hooks/api/useAuth";
import {
  useProperty,
  usePublishReadiness,
  useRelistProperty,
} from "@/hooks/api/useProperties";
import { useRunMatching } from "@/hooks/api/useSubscribers";
import { enabledAmenityLabels } from "@/lib/amenities";
import { PROPERTY_SECTIONS } from "@/lib/propertyFields";
import { isLandlord } from "@/lib/roles";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "details", label: "Details" },
  { id: "listing", label: "Listing" },
  { id: "management", label: "Management" },
  { id: "activity", label: "Activity" },
] as const;

type PropertyTab = (typeof TABS)[number]["id"];
const LANDLORD_TABS: PropertyTab[] = ["overview", "activity"];

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium tabular-nums">{value}</p>
    </div>
  );
}

export function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: property, isLoading, isError } = useProperty(id);
  const { data: readiness } = usePublishReadiness(id);
  const runMatching = useRunMatching();
  const relist = useRelistProperty();
  const { data: me } = useMe();
  const landlordView = isLandlord(me);

  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmRelist, setConfirmRelist] = useState(false);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (isError || !property) {
    return <p className="text-sm text-danger">Property not found.</p>;
  }

  const visibleTabs = TABS.filter((t) => !landlordView || LANDLORD_TABS.includes(t.id));
  const requested = (searchParams.get("tab") as PropertyTab) ?? "overview";
  const tab = visibleTabs.some((t) => t.id === requested) ? requested : "overview";
  const setTab = (t: PropertyTab) => setSearchParams({ tab: t });

  const title =
    [property.street, property.house_number].filter(Boolean).join(" ") ||
    property.reference ||
    "Property";
  // A let property that's been rented out or whose lease ended can go back on the
  // market — relisting resets its rental_status to "ready".
  const canRelist =
    property.rental_status === "rented" || property.rental_status === "ended";

  const onRelist = () =>
    relist.mutate(property.id, {
      onSuccess: () => toast.success("Property is back on the market"),
      onError: (e) => toast.error(e.message),
    });

  const rent =
    property.advertised_rent != null
      ? new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(property.advertised_rent)
      : "—";

  // --- Lifecycle rail: Draft → Published → Let → Ended ---
  const published = property.is_published;
  const status = property.rental_status; // "ready" | "rented" | "ended"
  const steps: LifecycleStep[] = [
    { key: "draft", label: "Draft", state: "done", tab: "details" },
    {
      key: "published",
      label: "Published",
      state: published ? "done" : "current",
      tab: "overview",
    },
    {
      key: "let",
      label: "Let",
      state:
        status === "rented" || status === "ended"
          ? "done"
          : published
            ? "current"
            : "upcoming",
      tab: "activity",
    },
    {
      key: "ended",
      label: "Ended",
      state: status === "ended" ? "done" : status === "rented" ? "current" : "upcoming",
      tab: "activity",
    },
  ];

  let hint: string | undefined;
  if (!published && readiness) {
    if (readiness.ready) {
      hint = "Ready to publish — open the checklist on the Overview tab.";
    } else {
      const total = readiness.checks.length;
      const ok = readiness.checks.filter((c) => c.ok).length;
      const blocker = readiness.checks.find((c) => !c.ok && c.blocking)?.label;
      hint = `To publish — ${ok} of ${total} ready.${blocker ? ` Waiting on ${blocker}.` : ""}`;
    }
  } else if (published && status === "ready") {
    hint = "Live — notify subscribers and review applications under Activity.";
  } else if (status === "rented") {
    hint = "Let — manage the contract under Activity.";
  } else if (status === "ended") {
    hint = "Tenancy ended — re-list to put it back on the market.";
  }

  const { amenities: amenityLabels } = enabledAmenityLabels(property.amenities ?? {});

  return (
    <div className="space-y-6">
      <Link
        to="/properties"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Properties
      </Link>

      <header className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {[property.postal_code, property.city].filter(Boolean).join(" · ") || "—"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge tone={property.is_published ? "success" : "neutral"} dot>
            {property.is_published ? "Published" : "Draft"}
          </Badge>
          {!landlordView && canRelist && (
            <Button
              size="sm"
              variant="outline"
              disabled={relist.isPending}
              onClick={() => setConfirmRelist(true)}
            >
              <RotateCcw className="size-4" />
              Re-list
            </Button>
          )}
          {!landlordView && (
            <Button
              size="sm"
              variant="ghost"
              disabled={runMatching.isPending}
              onClick={() =>
                runMatching.mutate(property.id, {
                  onSuccess: (r) =>
                    toast.success(`Notified ${r.matched} matching subscriber(s)`),
                  onError: () => toast.error("Matching failed"),
                })
              }
            >
              <Megaphone className="size-4" />
              Notify subscribers
            </Button>
          )}
        </div>
      </header>

      <LifecycleStepper
        steps={steps}
        hint={hint}
        onNavigate={(t) => setTab(t as PropertyTab)}
      />

      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              tab === t.id
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>At a glance</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-5 sm:grid-cols-4">
              <Stat label="Rent / mo" value={rent} />
              <Stat
                label="Surface"
                value={property.surface_m2 != null ? `${property.surface_m2} m²` : "—"}
              />
              <Stat label="Sector" value={property.free_sector ? "Free" : "Social"} />
              <Stat
                label="Marketplace"
                value={property.publish_to_marketplace ? "Listed" : "No"}
              />
            </CardContent>
          </Card>

          {amenityLabels.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Amenities</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {amenityLabels.map((a) => (
                  <Badge key={a} tone="neutral">
                    {a}
                  </Badge>
                ))}
              </CardContent>
            </Card>
          )}

          {!landlordView && <PublishReadinessCard propertyId={property.id} />}
        </div>
      )}

      {tab === "details" && !landlordView && (
        <div className="space-y-6">
          {PROPERTY_SECTIONS.map((section) => (
            <PropertySectionCard key={section.id} property={property} section={section} />
          ))}
          <FeaturesCard property={property} />
        </div>
      )}

      {tab === "listing" && !landlordView && (
        <div className="space-y-6">
          <MediaGallery propertyId={property.id} />
          <DescriptionsCard
            propertyId={property.id}
            descriptions={property.descriptions}
          />
          <RoomsCard propertyId={property.id} rooms={property.rooms} />
          <PublicationCard
            propertyId={property.id}
            isPublished={property.is_published}
            publishToMarketplace={property.publish_to_marketplace}
            channels={property.publication_channels ?? {}}
          />
        </div>
      )}

      {tab === "management" && !landlordView && (
        <div className="space-y-6">
          <ManagementCard propertyId={property.id} />
          <MeterReadingsCard propertyId={property.id} />
        </div>
      )}

      {tab === "activity" && <PropertyLifecycleCard propertyId={property.id} />}

      <ConfirmDialog
        open={confirmRelist}
        title="Re-list this property?"
        message="It goes back on the market — the rental status resets to ready."
        confirmLabel="Re-list"
        pending={relist.isPending}
        onConfirm={() => {
          onRelist();
          setConfirmRelist(false);
        }}
        onCancel={() => setConfirmRelist(false)}
      />
    </div>
  );
}
